import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  createProfileApi,
  getProfileApi,
  updateProfileApi,
  acceptUgcTermsApi,
} from "@/lib/api";

const STORAGE_KEY = "bom_profile_id";

const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [initializing, setInitializing] = useState(true);

  // Mount-only bootstrap: STORAGE_KEY is a module constant and getProfileApi
  // is a stable import, so the empty dependency array is exhaustive-deps
  // compliant. localStorage holds only an opaque profile UUID (no PII/tokens).
  useEffect(() => {
    const load = async () => {
      const id = localStorage.getItem(STORAGE_KEY);
      if (!id) {
        setInitializing(false);
        return;
      }
      try {
        const data = await getProfileApi(id);
        setProfile(data);
      } catch (e) {
        // Profile no longer exists on server — reset local state
        if (e?.response?.status === 404) {
          localStorage.removeItem(STORAGE_KEY);
        }
      } finally {
        setInitializing(false);
      }
    };
    load();
  }, []);

  const createProfile = useCallback(async (payload) => {
    const data = await createProfileApi(payload);
    localStorage.setItem(STORAGE_KEY, data.id);
    setProfile(data);
    return data;
  }, []);

  const updateProfile = useCallback(
    async (payload) => {
      if (!profile) return null;
      const data = await updateProfileApi(profile.id, payload);
      setProfile(data);
      return data;
    },
    [profile]
  );

  const clearProfile = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setProfile(null);
  }, []);

  // Phase 10 (UGC compliance): one-time acceptance of the community Terms.
  // Server sets the timestamp; the returned profile refreshes local state.
  const acceptUgcTerms = useCallback(async () => {
    if (!profile) return null;
    const data = await acceptUgcTermsApi(profile.id);
    setProfile(data);
    return data;
  }, [profile]);

  return (
    <ProfileContext.Provider
      value={{ profile, initializing, createProfile, updateProfile, clearProfile, acceptUgcTerms }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within ProfileProvider");
  return ctx;
};
